import axios from 'axios';
import { notification } from 'antd';
import config from '../config';

// Add a flag to prevent multiple redirects
let isRedirecting = false;

const createApiInstance = (baseURL = config.API_URL) => {
  const instance = axios.create({
    baseURL,
    timeout: 60000, // 60 seconds
    withCredentials: true, // Important for handling cookies
  });

  instance.defaults.withCredentials = true;

  // Request interceptor
  instance.interceptors.request.use(
    (reqConfig) => {
      const token = localStorage.getItem(config.AUTH_TOKEN_KEY);
      if (token) {
        reqConfig.headers['Authorization'] = `Bearer ${token}`;
      }
      return reqConfig;
    },
    (error) => {
      console.error('Request Error:', error);
      notification.error({ message: 'Request Error', description: error.message });
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response.data,
    (error) => {
      // Don't handle errors if we're already redirecting
      if (!isRedirecting) {
        // Check if this is an impersonation request
        const isImpersonationRequest = error.config?.url?.includes('/admin/impersonate');
        const isImpersonating = localStorage.getItem('is_impersonating') === 'true';
        
        if (!isImpersonationRequest) {
          handleApiError(error, isImpersonating);
        } else {
          console.log('Handling error during impersonation request');
        }
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

const handleApiError = (error, isImpersonating = false) => {
  const { response } = error;
  const notificationParams = {
    message: 'Error',
    description: 'An unexpected error occurred.',
  };

  if (response) {
    // Handle 401/403 Unauthorized errors
    if (unauthorizedCodes.includes(response.status)) {
      // If we're impersonating, just show a notification without redirect
      if (isImpersonating) {
        console.log('Authentication error while impersonating. Not logging out.');
        notificationParams.message = 'Authentication Issue';
        notificationParams.description = 'There was an issue with the current session.';
        notification.error(notificationParams);
        return;
      }
      
      // Prevent redirect loop by checking if we've already cleared auth
      const hasAuthToken = !!localStorage.getItem(config.AUTH_TOKEN_KEY);
      if (!hasAuthToken) {
        console.log('Already logged out, not redirecting again');
        return;
      }
      
      notificationParams.message = 'Authentication Failed';
      notificationParams.description = response.data.message || 'Please login again';

      // Set redirecting flag to prevent multiple redirects
      isRedirecting = true;
      
      // Clear auth data
      clearAuthData();
      
      notification.error(notificationParams);
      
      // Redirect to login page
      console.log('Redirecting to login page due to auth error');
      window.location.href = '/login';
      
      // Reset the redirecting flag after a delay
      setTimeout(() => {
        isRedirecting = false;
      }, 5000);
    } 
    // Handle other status codes
    else if (response.status === 404) {
      notificationParams.message = 'Not Found';
      notificationParams.description = response.data.message || 'The requested resource was not found.';
      notification.error(notificationParams);
    } else if (response.status === 500) {
      notificationParams.message = 'Internal Server Error';
      notificationParams.description = response.data.message || 'Something went wrong on the server.';
      notification.error(notificationParams);
    } else if (response.status === 422) {
      notificationParams.message = 'Validation Error';
      notificationParams.description = response.data.message || 'Please check your input.';
      notification.error(notificationParams);
    } else {
      notificationParams.message = `Error (${response.status})`;
      notificationParams.description = response.data.message || 'An unexpected error occurred.';
      notification.error(notificationParams);
    }
  } else {
    // Network error or request cancelled
    notificationParams.message = 'Network Error';
    notificationParams.description = 'Unable to connect to the server. Please check your internet connection.';
    notification.error(notificationParams);
  }
};

// Helper function to clear all auth data
const clearAuthData = () => {
  // Clear all relevant storage items
  localStorage.removeItem(config.AUTH_TOKEN_KEY);
  localStorage.removeItem('user_roles');
  localStorage.removeItem('user_permissions');
  localStorage.removeItem('user');
  
  // Clear impersonation data
  localStorage.removeItem('is_impersonating');
  localStorage.removeItem('_admin_token');
  localStorage.removeItem('_admin_user');
  localStorage.removeItem('_admin_roles');
  localStorage.removeItem('_admin_permissions');
  localStorage.removeItem('_admin_return_path');
  
  // Clear all cookies
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim();
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
};

const unauthorizedCodes = [401, 403];
const apiService = createApiInstance();

export default apiService;